/**
 * useRetouchFlow Hook
 *
 * Core hook for the returning visitor discount flow.
 * Consumes visit data from UserAnalytics and manages modal-specific state.
 *
 * NOTE: Visit tracking is now handled by UserAnalytics (global).
 * This hook only manages modal state (shown, claimed) and logic.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useSearchParams } from 'next/navigation';
import { useInternalPathname } from 'hooks/navigation/useInternalPathname';
import { analytics } from 'lib/analytics/analytics';
import { useUserAnalyticsContext } from 'components/shared/UserAnalytics';
import {
  RETOUCH_STORAGE_KEY,
  VISITS_THRESHOLD,
  RETOUCH_AFFILIATE_CODE,
  BUY_PAGE_PATTERN,
  ANALYTICS_EVENTS,
  GTM_EVENTS,
  RetouchFlowData,
  DEFAULT_RETOUCH_DATA,
} from './constants';

// =============================================================================
// localStorage Helpers (modal state only)
// =============================================================================

const getStorageData = (): RetouchFlowData => {
  if (typeof window === 'undefined') return DEFAULT_RETOUCH_DATA;

  try {
    const stored = window.localStorage.getItem(RETOUCH_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as RetouchFlowData;
      // Handle migration from old format (with visitCount) to new format
      return {
        modalShown: parsed.modalShown ?? false,
        discountClaimed: parsed.discountClaimed ?? false,
      };
    }
  } catch (e) {
    console.error('[RetouchFlow] Error reading localStorage:', e);
  }

  return DEFAULT_RETOUCH_DATA;
};

const setStorageData = (data: RetouchFlowData): void => {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(RETOUCH_STORAGE_KEY, JSON.stringify(data));
  } catch (e) {
    console.error('[RetouchFlow] Error writing localStorage:', e);
  }
};

// =============================================================================
// GTM DataLayer Helper
// =============================================================================

const pushToDataLayer = (
  event: string,
  data?: Record<string, unknown>,
): void => {
  if (typeof window === 'undefined') return;

  try {
    (
      window as Window & { dataLayer?: Record<string, unknown>[] }
    ).dataLayer?.push({
      event,
      ...data,
    });
  } catch (e) {
    console.error('[RetouchFlow] Error pushing to dataLayer:', e);
  }
};

// =============================================================================
// Helper Functions
// =============================================================================

const isBuyPage = (pathname: string): boolean => {
  return pathname.includes(BUY_PAGE_PATTERN);
};

const getDeviceType = (): string => {
  if (typeof window === 'undefined') return 'unknown';
  const width = window.innerWidth;
  if (width < 768) return 'mobile';
  if (width < 1024) return 'tablet';
  return 'desktop';
};

// =============================================================================
// Hook Interface
// =============================================================================

export interface UseRetouchFlowReturn {
  /** Current Ring page visit count (from UserAnalytics) */
  visitCount: number;

  /** Whether the modal should be shown based on all conditions */
  shouldShowModal: boolean;

  /** Whether the modal is currently visible */
  isModalVisible: boolean;

  /** Show the modal */
  showModal: () => void;

  /** Hide the modal */
  hideModal: () => void;

  /** Mark modal as shown (prevents future showing) */
  markModalShown: () => void;

  /**
   * Reset modal state (call on Add to Cart).
   * Note: This no longer resets visit count as that's managed by UserAnalytics.
   */
  resetModalState: () => void;

  /** Claim the discount (redirects with affiliate code) */
  claimDiscount: () => void;

  /** Whether an affiliate code is already present in URL */
  hasExistingAffiliateCode: boolean;

  /** Whether this user was detected as pre-existing (from UserAnalytics) */
  isPreExistingUser: boolean;
}

// =============================================================================
// Main Hook
// =============================================================================

export const useRetouchFlow = (): UseRetouchFlowReturn => {
  const pathname = useInternalPathname();
  const searchParams = useSearchParams();

  // Get visit data from global UserAnalytics
  const { ringVisits, isPreExistingUser } = useUserAnalyticsContext();

  // Local state for modal-specific data
  const [modalData, setModalData] =
    useState<RetouchFlowData>(DEFAULT_RETOUCH_DATA);
  const [isModalVisible, setIsModalVisible] = useState(false);

  // Check if affiliateCode is present in URL
  const hasExistingAffiliateCode = Boolean(
    searchParams?.get('affiliateCode') || searchParams?.get('affiliate'),
  );

  // Load modal state from localStorage on mount
  useEffect(() => {
    const storedData = getStorageData();
    setModalData(storedData);
  }, []);

  // Track if we've already fired certain one-time events
  const hasTrackedEligible = useRef(false);
  const hasTrackedAffiliateSuppress = useRef(false);

  // Check if user meets threshold
  const meetsThreshold = ringVisits >= VISITS_THRESHOLD;

  // Track when user becomes eligible (reaches threshold)
  useEffect(() => {
    if (!meetsThreshold) return;
    if (hasTrackedEligible.current) return;
    if (!modalData.modalShown) {
      // Only track if they haven't already seen/dismissed the modal
      hasTrackedEligible.current = true;

      const analyticsData = {
        visitCount: ringVisits,
        threshold: VISITS_THRESHOLD,
        page: pathname,
        device: getDeviceType(),
        isPreExistingUser,
        isBuyPage: isBuyPage(pathname),
      };

      analytics.track(ANALYTICS_EVENTS.ELIGIBLE, analyticsData);
      pushToDataLayer(GTM_EVENTS.ELIGIBLE, analyticsData);
    }
  }, [
    meetsThreshold,
    ringVisits,
    pathname,
    isPreExistingUser,
    modalData.modalShown,
  ]);

  // Track when modal is suppressed due to existing affiliate code
  useEffect(() => {
    if (!meetsThreshold) return;
    if (!hasExistingAffiliateCode) return;
    if (hasTrackedAffiliateSuppress.current) return;
    if (modalData.modalShown) return;
    if (!isBuyPage(pathname)) return;

    hasTrackedAffiliateSuppress.current = true;

    const existingAffiliateCode =
      searchParams?.get('affiliateCode') || searchParams?.get('affiliate');

    const analyticsData = {
      visitCount: ringVisits,
      threshold: VISITS_THRESHOLD,
      page: pathname,
      device: getDeviceType(),
      isPreExistingUser,
      existingAffiliateCode: existingAffiliateCode || '',
    };

    analytics.track(ANALYTICS_EVENTS.SUPPRESSED_BY_AFFILIATE, analyticsData);
    pushToDataLayer(GTM_EVENTS.SUPPRESSED_BY_AFFILIATE, analyticsData);
  }, [
    meetsThreshold,
    hasExistingAffiliateCode,
    ringVisits,
    pathname,
    searchParams,
    isPreExistingUser,
    modalData.modalShown,
  ]);

  // Determine if modal should be shown
  // Now uses ringVisits from UserAnalytics instead of local tracking
  const shouldShowModal =
    meetsThreshold &&
    !modalData.modalShown &&
    !hasExistingAffiliateCode &&
    isBuyPage(pathname);

  // Show modal
  const showModal = useCallback(() => {
    setIsModalVisible(true);

    // Track analytics
    const analyticsData = {
      visitCount: ringVisits,
      threshold: VISITS_THRESHOLD,
      page: pathname,
      device: getDeviceType(),
      isPreExistingUser,
    };

    analytics.track(ANALYTICS_EVENTS.MODAL_SHOWN, analyticsData);
    pushToDataLayer(GTM_EVENTS.MODAL_SHOWN, analyticsData);
  }, [ringVisits, pathname, isPreExistingUser]);

  // Hide modal
  const hideModal = useCallback(() => {
    setIsModalVisible(false);
  }, []);

  // Mark modal as shown (prevents future showing)
  const markModalShown = useCallback(() => {
    const newData: RetouchFlowData = {
      ...modalData,
      modalShown: true,
    };
    setStorageData(newData);
    setModalData(newData);
    hideModal();

    // Track analytics
    const analyticsData = {
      visitCount: ringVisits,
      threshold: VISITS_THRESHOLD,
      page: pathname,
      device: getDeviceType(),
      isPreExistingUser,
    };

    analytics.track(ANALYTICS_EVENTS.MODAL_CLOSED, analyticsData);
    pushToDataLayer(GTM_EVENTS.MODAL_CLOSED, analyticsData);
  }, [modalData, hideModal, ringVisits, pathname, isPreExistingUser]);

  // Reset modal state (called on Add to Cart)
  // Note: This no longer resets visit count - that's managed globally by UserAnalytics
  const resetModalState = useCallback(() => {
    // Just reset the modal state, not the visit count
    const newData: RetouchFlowData = {
      ...DEFAULT_RETOUCH_DATA,
      // Preserve modalShown if they've already seen and dismissed the modal
      modalShown: modalData.modalShown,
    };
    setStorageData(newData);
    setModalData(newData);
  }, [modalData.modalShown]);

  // Claim discount (redirect with affiliate code)
  const claimDiscount = useCallback(() => {
    // Track CTA click immediately (before any state changes)
    const ctaAnalyticsData = {
      visitCount: ringVisits,
      threshold: VISITS_THRESHOLD,
      page: pathname,
      device: getDeviceType(),
      isPreExistingUser,
      affiliateCode: RETOUCH_AFFILIATE_CODE,
    };

    analytics.track(ANALYTICS_EVENTS.CTA_CLICKED, ctaAnalyticsData);
    pushToDataLayer(GTM_EVENTS.CTA_CLICKED, ctaAnalyticsData);

    // Mark as shown and claimed
    const newData: RetouchFlowData = {
      modalShown: true,
      discountClaimed: true,
    };
    setStorageData(newData);
    setModalData(newData);
    hideModal();

    // Build redirect URL
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('affiliateCode', RETOUCH_AFFILIATE_CODE);
    const redirectUrl = currentUrl.toString();

    // Track discount claimed (redirect about to happen)
    const claimedAnalyticsData = {
      visitCount: ringVisits,
      threshold: VISITS_THRESHOLD,
      page: pathname,
      device: getDeviceType(),
      isPreExistingUser,
      affiliateCode: RETOUCH_AFFILIATE_CODE,
      redirectUrl,
    };

    analytics.track(ANALYTICS_EVENTS.DISCOUNT_CLAIMED, claimedAnalyticsData);
    pushToDataLayer(GTM_EVENTS.DISCOUNT_CLAIMED, claimedAnalyticsData);

    // Redirect
    window.location.href = redirectUrl;
  }, [hideModal, ringVisits, pathname, isPreExistingUser]);

  return {
    visitCount: ringVisits,
    shouldShowModal,
    isModalVisible,
    showModal,
    hideModal,
    markModalShown,
    resetModalState,
    claimDiscount,
    hasExistingAffiliateCode,
    isPreExistingUser,
  };
};

export default useRetouchFlow;
